import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import { FiFilter, FiX, FiSearch } from 'react-icons/fi';
import Pagination from '../components/common/Pagination';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: '',
    maxPrice: '',
    size: '',
    search: searchParams.get('search') || '',
    sort: 'newest',
    onSale: searchParams.get('onSale') === 'true',
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 1 });

  useEffect(() => {
    loadProducts(1);
    loadCategories();
  }, [filters]);

  const loadProducts = async (page = pagination.page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);
      if (filters.category) params.append('category', filters.category);
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
      if (filters.size) params.append('size', filters.size);
      if (filters.search) params.append('search', filters.search);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.onSale) params.append('onSale', 'true');

      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data.products || []);
      setPagination({
        page: response.data.page,
        limit: pagination.limit,
        total: response.data.total,
        pages: response.data.pages,
      });
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      minPrice: '',
      maxPrice: '',
      size: '',
      search: '',
      sort: 'newest',
      onSale: false,
    });
  };

  return (
    <div className="min-h-screen bg-dark-bg pt-12 pb-24">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">COLLECTION</h1>
            <p className="text-gray-400">Curated for performance and style.</p>
          </motion.div>

          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="md:hidden btn-secondary w-full flex items-center justify-center space-x-2"
          >
            <FiFilter />
            <span>Filters</span>
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside
            className={`${filtersOpen ? 'fixed inset-0 z-50 bg-dark-bg p-6' : 'hidden'
              } md:block md:static w-full md:w-72 bg-dark-card border border-white/10 p-6 rounded-xl h-fit sticky top-24 overflow-y-auto`}
          >
            <div className="flex items-center justify-between mb-8 md:mb-6">
              <h2 className="font-display font-bold text-xl text-white">FILTERS</h2>
              <button
                onClick={() => setFiltersOpen(false)}
                className="md:hidden text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">SEARCH</label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Keywords..."
                  className="input-field pl-10"
                />
                <FiSearch className="absolute left-3 top-3.5 text-gray-500" />
              </div>
            </div>

            {/* Category Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">CATEGORY</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="input-field cursor-pointer"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">PRICE (PKR)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  placeholder="Min"
                  className="input-field"
                />
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  placeholder="Max"
                  className="input-field"
                />
              </div>
            </div>

            {/* Size Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">SIZE</label>
              <select
                value={filters.size}
                onChange={(e) => handleFilterChange('size', e.target.value)}
                className="input-field cursor-pointer"
              >
                <option value="">Any Size</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </div>

            {/* Sort */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">SORT BY</label>
              <select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="input-field cursor-pointer"
              >
                <option value="newest">Newest Drops</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            {/* On Sale Filter */}
            <div className="mb-8">
              <label className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.onSale}
                  onChange={(e) => handleFilterChange('onSale', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-primary-light focus:ring-primary-light"
                />
                <span className="text-gray-300 group-hover:text-white transition-colors">On Sale Only</span>
              </label>
            </div>

            <button onClick={clearFilters} className="btn-secondary w-full text-sm">
              Reset Filters
            </button>
          </aside>

          {/* Products Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="skeleton h-96 rounded-xl" />
                  ))}
                </div>
              ) : products.length > 0 ? (
                <div className="space-y-12">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <AnimatePresence>
                      {products.map((product) => (
                        <ProductCard key={product._id} product={product} />
                      ))}
                    </AnimatePresence>
                  </div>
                  
                  <Pagination 
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={(page) => loadProducts(page)}
                    loading={loading}
                  />
                </div>
              ) : (
              <div className="text-center py-24 bg-dark-card rounded-xl border border-white/5">
                <p className="text-gray-400 text-xl font-light">No gear found matching criteria.</p>
                <button onClick={clearFilters} className="mt-4 text-primary-light hover:underline">Clear all filters</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
